// Runtime validation for INBOUND (client → server) messages. The wire is untrusted: the
// world server JSON.parses whatever a socket sends, so every field a handler reads must be
// shape-checked here first. Output (ServerMessage) is server-authored and needs no schema.
//
// Single source of truth for the *shape* stays the hand-written `ClientMessage` type in
// index.ts; the `coherence` block at the bottom is a COMPILE-TIME proof that this schema and
// that type are mutually assignable, so the two can never silently drift. Add a message
// variant in one place and the build fails until the other matches.
//
// Every numeric field is `.int()` on purpose: it rejects NaN / Infinity / floats, which is
// what closes the "send qty:{} → cost becomes NaN → `wallet.dp < NaN` is false → spend
// bypassed" class of exploit. Unknown extra keys are stripped (zod default), so a newer
// client adding a field never trips an older server.
import { z } from "zod";
const int = z.number().int();
// ── shared building blocks ────────────────────────────────────────────────────
const facing = z.enum(["n", "s", "e", "w", "ne", "nw", "se", "sw"]);
const chibiSlotChoice = z.object({ variant: z.string(), color: int });
const chibiLook = z.object({
    gender: z.enum(["cm", "cw"]),
    slots: z.record(z.string(), chibiSlotChoice),
});
const avatarSelection = z.object({
    parts: z.array(int),
    name: z.string(),
    chibi: chibiLook.optional(),
    sleeveId: z.string().optional(),
});
const placeRef = z.object({ controller: int, location: int, sequence: int });
const duelFormat = z.enum(["world", "free"]);
const ladder = z.enum(["world", "free-single", "free-match"]);
const duelChoice = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("idle"), cmd: int, index: int.optional() }),
    z.object({ kind: z.literal("battle"), cmd: int, index: int.optional() }),
    z.object({ kind: z.literal("chain"), index: int }),
    z.object({ kind: z.literal("yesno"), yes: z.boolean() }),
    z.object({ kind: z.literal("option"), index: int }),
    z.object({ kind: z.literal("position"), pos: int }),
    z.object({ kind: z.literal("selectCards"), indices: z.array(int) }),
    z.object({ kind: z.literal("place"), places: z.array(placeRef) }),
    z.object({ kind: z.literal("unselect"), index: int.nullable() }),
    z.object({ kind: z.literal("cancel") }),
    z.object({ kind: z.literal("tp"), first: z.boolean() }),
    z.object({ kind: z.literal("counter"), counts: z.array(int) }),
    z.object({ kind: z.literal("race"), value: z.string() }),
    z.object({ kind: z.literal("attrib"), value: int }),
    z.object({ kind: z.literal("announceCard"), code: int }),
    z.object({ kind: z.literal("announceNumber"), index: int }),
    z.object({ kind: z.literal("sort"), order: z.array(int).nullable() }),
]);
const tradeOfferCard = z.object({ cardId: int, qty: int });
const tradeOfferCosmetic = z.object({ kind: z.enum(["sleeve", "dueldisk", "outfit"]), itemId: z.string(), qty: int });
const tradeOfferFoil = z.object({ cardId: int, grade: int, qty: int });
const cardDismantleItem = z.object({
    cardId: int.positive().max(99_999_999),
    qty: int.positive().max(1_000_000),
    foil: int.min(1).max(3).optional(),
});
// ── the inbound message union ──────────────────────────────────────────────────
export const clientMessageSchema = z.discriminatedUnion("t", [
    z.object({ t: z.literal("Hello"), clientVersion: z.string() }),
    z.object({ t: z.literal("Auth"), token: z.string() }),
    z.object({ t: z.literal("Logout") }),
    z.object({ t: z.literal("EnterMap"), mapId: z.string(), x: int.optional(), y: int.optional(), facing: facing.optional() }),
    z.object({ t: z.literal("Move"), x: int, y: int, facing }),
    z.object({ t: z.literal("Say"), channel: z.enum(["map", "world", "shout"]), text: z.string() }),
    z.object({ t: z.literal("Whisper"), toUserId: z.string(), text: z.string() }),
    z.object({ t: z.literal("AvatarSet"), selection: avatarSelection }),
    z.object({ t: z.literal("SetName"), name: z.string() }),
    z.object({ t: z.literal("InventoryGet") }),
    z.object({ t: z.literal("DeckList"), format: duelFormat.optional() }),
    z.object({ t: z.literal("DeckSave"), name: z.string(), main: z.array(int), extra: z.array(int).optional(), side: z.array(int).optional(), format: duelFormat.optional() }),
    z.object({ t: z.literal("DeckDelete"), name: z.string(), format: duelFormat.optional() }),
    z.object({ t: z.literal("ShopList"), shop: z.enum(["kame", "academy"]).optional() }),
    z.object({ t: z.literal("PackOpen"), packId: z.string() }),
    z.object({ t: z.literal("PackOpenAll") }),
    z.object({ t: z.literal("ShopBuy"), itemId: z.string(), qty: int }),
    z.object({ t: z.literal("ClaimStarterDeck"), deckIds: z.array(z.string()) }),
    z.object({ t: z.literal("CardCraft"), cardId: int }),
    z.object({ t: z.literal("CardDismantle"), cardId: int, qty: int, foil: int.optional() }),
    z.object({ t: z.literal("CardDismantleBulk"), items: z.array(cardDismantleItem).min(1).max(500) }),
    z.object({ t: z.literal("CardDismantleExtras") }),
    z.object({ t: z.literal("BoxOpen"), boxId: z.enum(["outfit", "dueldisk", "sleeve"]) }),
    z.object({ t: z.literal("BoxPick"), boxId: z.enum(["outfit", "dueldisk", "sleeve"]), kind: z.enum(["sleeve", "dueldisk", "outfit"]), itemId: z.string() }),
    z.object({ t: z.literal("CosmeticInventoryGet") }),
    z.object({ t: z.literal("DuelInvite"), toUserId: z.string(), deck: z.string().optional() }),
    z.object({ t: z.literal("DuelPractice"), npc: z.string().optional(), deck: z.string().optional(), format: duelFormat.optional() }),
    z.object({ t: z.literal("DuelAccept"), fromUserId: z.string(), deck: z.string().optional() }),
    z.object({ t: z.literal("DuelDecline"), fromUserId: z.string() }),
    z.object({ t: z.literal("DuelAction"), choice: duelChoice }),
    z.object({ t: z.literal("DuelSay"), text: z.string() }),
    // Client-error telemetry (beta): the client forwards its first few console errors so a player's
    // broken session leaves server-side evidence instead of relying on bug reports. Length-capped
    // here AND rate-limited server-side.
    z.object({ t: z.literal("ClientLog"), text: z.string().max(600) }),
    z.object({ t: z.literal("DuelLeave") }),
    z.object({ t: z.literal("ReplayGet") }),
    z.object({ t: z.literal("RankGet"), format: duelFormat.optional(), ladder: ladder.optional() }),
    z.object({ t: z.literal("QueueJoin"), mode: z.enum(["ranked", "unranked"]), format: duelFormat.optional(), bestOf: z.union([z.literal(1), z.literal(3)]).optional(), deck: z.string().optional() }),
    z.object({ t: z.literal("QueueLeave") }),
    z.object({ t: z.literal("SideSubmit"), main: z.array(int), extra: z.array(int), side: z.array(int), goFirst: z.boolean().optional() }),
    z.object({ t: z.literal("LobbyHost"), format: duelFormat.optional(), bestOf: z.union([z.literal(1), z.literal(3)]).optional(), deck: z.string().optional() }),
    z.object({ t: z.literal("LobbyCancel") }),
    z.object({ t: z.literal("LobbyList") }),
    z.object({ t: z.literal("LobbyJoin"), lobbyId: z.string(), deck: z.string().optional() }),
    z.object({ t: z.literal("LeaderboardGet"), limit: int.optional(), ladder: ladder.optional() }),
    z.object({ t: z.literal("PlayerProfileGet"), userId: z.string() }),
    z.object({ t: z.literal("TradeRequest"), toUserId: z.string() }),
    z.object({ t: z.literal("TradeRespond"), fromUserId: z.string(), accept: z.boolean() }),
    z.object({ t: z.literal("TradeSetOffer"), cards: z.array(tradeOfferCard), cosmetics: z.array(tradeOfferCosmetic).optional(), foils: z.array(tradeOfferFoil).optional() }),
    z.object({ t: z.literal("TradeLock"), locked: z.boolean() }),
    z.object({ t: z.literal("TradeConfirm") }),
    z.object({ t: z.literal("TradeCancel") }),
    z.object({ t: z.literal("GoToRequest"), toUserId: z.string() }),
    z.object({ t: z.literal("GoToRespond"), fromUserId: z.string(), accept: z.boolean() }),
    z.object({ t: z.literal("Ping"), nonce: int }),
]);
/** Parse + validate a raw inbound frame. Returns the typed message or a reason string. */
export function parseClientMessage(raw) {
    let json;
    try {
        json = JSON.parse(raw);
    }
    catch {
        return { ok: false, error: "could not parse JSON" };
    }
    const r = clientMessageSchema.safeParse(json);
    if (!r.success) {
        const i = r.error.issues[0];
        return { ok: false, error: i ? `${i.path.join(".") || "message"}: ${i.message}` : "invalid message" };
    }
    return { ok: true, value: r.data };
}
const _schemaSatisfiesType = null;
const _typeSatisfiesSchema = null;
void _schemaSatisfiesType;
void _typeSatisfiesSchema;
//# sourceMappingURL=schema.js.map